/**
 * Trill detection: FFT the amplitude envelope, not the audio.
 *
 * FFT-ing raw audio finds pitch. A trill's identity is not its pitch — a lip
 * trill and a hummed note can sit on the same fundamental. Its identity is that
 * the *loudness* pulses, twenty to thirty times a second, and that is a
 * frequency in the envelope rather than in the waveform.
 *
 * ```
 * envelope rate    sampleRate / 128        ~375 Hz, computed, never assumed
 * FFT size         256                     -> 683 ms window
 * bin width        envRate / 256           ~1.46 Hz
 * hop              every 32 blocks         ~85 ms
 * window           Hann
 * band             18-36 Hz
 * ```
 *
 * The payoff is `cycles`: a real count of how many times the lips actually
 * closed, from `dur x modRate`. Four hundred milliseconds at 25 Hz is ten
 * vibrations, so "brrrrrrrrrr" gets ten r's because ten happened — the
 * letterform count stops being a guess about how emphatic to look.
 *
 * ### What detects a trill, and what does not
 *
 * SPEC1.md says: peak in band, above an absolute floor, at least 3x the band
 * median. Run against a recorded lip trill that number does not hold up.
 *
 *     mark      modRate median   range across hops   peak/median
 *     trill        20.7 Hz         20.5 .. 21.1         2.31
 *     ah           22.4 Hz         17.1 .. 22.6         1.50
 *     sss          23.2 Hz         23.2 .. 29.7         1.51
 *     whistle      21.5 Hz         13.9 .. 31.3         1.40
 *     rest         27.4 Hz         14.6 .. 37.0         1.69
 *     clap         31.8 Hz         11.3 .. 35.5         1.72
 *
 * Every label reports a peak somewhere in the band, because noise always has a
 * largest bin. On magnitude alone the real trill (2.31) fails the 3x test while
 * a clap (1.72) sits close behind it — the threshold would reject the sound and
 * very nearly accept the artefact.
 *
 * Look at the third column instead. The trill's peak holds within **0.6 Hz
 * across four consecutive windows**; everything else wanders over five to
 * twenty-four. That is the discriminator, and it is not an invention — SPEC1.md
 * already names stability as this phase's acceptance criterion ("stable within
 * +/-3 Hz across a 1 s sustain"). This promotes it from the test to the thing
 * being tested, and keeps magnitude as a weaker supporting gate.
 *
 * It is also the physically motivated choice. A trill is an oscillator: lip
 * tissue against airflow, running at whatever rate its mass and tension fix. Its
 * rate is stable *because* it is a resonance. A wandering peak is not a slow
 * trill, it is the largest bin of something that has no rate at all.
 */

import { config } from '../core/config.js';
import { magnitudeSpectrum, refinePeak } from './fft.js';

/**
 * One analysis hop's worth of findings.
 *
 * @typedef {object} Hop
 * @property {number} t            performance.now() at the CENTRE of the window
 * @property {number} modRate      Hz, sub-bin interpolated
 * @property {number} magnitude    peak height in envelope units
 * @property {number} ratio        peak over band median
 * @property {number} stability    0..1, see stabilityOf()
 * @property {boolean} trill       committed after hysteresis
 */

/**
 * How tightly a run of rates agrees, as 0..1.
 *
 * Expressed against `RATE_TOLERANCE` so it reads as a fraction rather than as a
 * spread in Hz that has to be compared against something else to mean anything.
 * A spread of zero is 1; a spread at the tolerance is 0.
 *
 * Spread rather than standard deviation: with three or four hops, a deviation
 * is barely distinguishable from its own estimation error, and the question
 * being asked — did every window agree — is about the extremes.
 *
 * @param {number[]} rates
 * @returns {number}
 */
export function stabilityOf(rates) {
  if (rates.length < 2) return 0;
  let lo = rates[0];
  let hi = rates[0];
  for (const r of rates) {
    if (r < lo) lo = r;
    if (r > hi) hi = r;
  }
  return Math.max(0, 1 - (hi - lo) / config.gesture.trill.RATE_TOLERANCE);
}

export class EnvelopeAnalyser {
  /**
   * @param {object} [o]
   * @param {number} [o.rate]  envelope samples per second — from the context,
   *   never assumed. A hardcoded 375 turns every modRate into a plausible lie.
   * @param {(event: import('../core/types.js').GestureEvent) => void} [o.onGesture]
   */
  constructor({ rate = 375, onGesture } = {}) {
    this.onGesture = onGesture ?? (() => {});
    /** Most recent hops, newest last. @type {Hop[]} */
    this.hops = [];
    /** The trill currently being accumulated, if any. */
    this.active = null;
    this._scratch = {};
    this.setRate(rate);
  }

  setRate(rate) {
    const n = config.envelope.FFT_SIZE;
    this.rate = rate;
    this.binHz = rate / n;
    this.windowMs = (n / rate) * 1000;

    /** The analysis window, filled circularly. */
    this._buf = new Float64Array(n);
    this._i = 0;
    this._filled = 0;
    /** Samples since the last hop. */
    this._since = 0;
    /** performance.now() of the newest sample in the buffer. */
    this._lastT = 0;

    this.hops = [];
    this.active = null;
  }

  /**
   * The bins the band covers, recomputed on every hop rather than cached.
   *
   * Two divisions per hop, twelve hops a second — nothing, and it is what makes
   * the band a live control rather than something fixed when the microphone
   * opened. The band is the one Phase 5 constant SPEC1.md names for Phase 8, and
   * a slider that needed the microphone restarting to take effect is a slider
   * you cannot use while making the sound you are tuning against.
   */
  get loBin() {
    return Math.max(1, Math.ceil(config.envelope.BAND_LO / this.binHz));
  }

  get hiBin() {
    return Math.min(
      this._buf.length / 2 - 1,
      Math.max(this.loBin, Math.floor(config.envelope.BAND_HI / this.binHz)),
    );
  }

  /** Ready once a full window exists. Before that there is nothing to transform. */
  get ready() {
    return this._filled >= this._buf.length;
  }

  /**
   * Fold in one envelope sample. Returns a Hop on the samples where the window
   * advances far enough to warrant one, otherwise null.
   *
   * @param {import('../core/types.js').EnvelopeSample} sample
   * @returns {?Hop}
   */
  push(sample) {
    this._buf[this._i] = sample.rms;
    this._i = (this._i + 1) % this._buf.length;
    if (this._filled < this._buf.length) this._filled++;
    this._lastT = sample.t;
    this._since++;

    if (!this.ready || this._since < config.envelope.HOP_BLOCKS) return null;
    this._since = 0;
    return this._analyse();
  }

  /** Transform the current window. */
  _analyse() {
    const n = this._buf.length;

    // Unwrap oldest-first. The FFT needs the window in time order; a circular
    // buffer transformed in place would put a discontinuity at an arbitrary
    // point inside it, which is a step function, which is broadband.
    const win = this._scratch.win?.length === n ? this._scratch.win : new Float64Array(n);
    for (let k = 0; k < n; k++) win[k] = this._buf[(this._i + k) % n];
    this._scratch.win = win;

    const mag = magnitudeSpectrum(win, this._scratch);
    this._scratch.mag = mag;

    // Mean level of the window, so the peak can be expressed as a fraction of
    // it rather than as an absolute in envelope units.
    //
    // For an envelope of the form level * (1 + d*sin(wt)) the mean is `level`
    // and the peak magnitude is `level * d`, so their ratio is exactly the
    // modulation depth: 0 for a steady sound, 1 for one that goes fully silent
    // between pulses. That is a property of the sound. The raw magnitude is a
    // property of how far away the microphone was and where its gain was set,
    // which is why the spec's absolute PEAK_MIN_MAG could not be made to hold
    // across two takes.
    let mean = 0;
    for (let k = 0; k < n; k++) mean += win[k];
    mean /= n;

    // Read once, not per iteration — they are getters now, and `hiBin` in a
    // loop condition would recompute the band edge on every bin.
    const loBin = this.loBin;
    const hiBin = this.hiBin;

    let peak = -1;
    let peakBin = loBin;
    const band = [];
    for (let k = loBin; k <= hiBin; k++) {
      band.push(mag[k]);
      if (mag[k] > peak) {
        peak = mag[k];
        peakBin = k;
      }
    }
    band.sort((a, b) => a - b);
    const median = band[band.length >> 1];

    const modRate = refinePeak(mag, peakBin) * this.binHz;
    // Timestamped at the CENTRE of the window, not its edge. The window covers
    // 683 ms of the past; attributing its finding to either end misplaces the
    // sound by a third of a second, and `config.latency.trill` is already half
    // a window for exactly this reason.
    const t = this._lastT - this.windowMs / 2;

    // Stability over a window of its own, not over the hysteresis count. They
    // answer different questions: hysteresis asks how long a decision must hold
    // before it is acted on, stability asks how many measurements have to agree
    // before there is a decision at all.
    const window = Math.max(2, config.gesture.trill.STABILITY_HOPS);
    const recent = [...this.hops.slice(-(window - 1)), { modRate }];
    const stability = recent.length < window ? 0 : stabilityOf(recent.map((h) => h.modRate));

    const hop = {
      t,
      modRate,
      magnitude: peak,
      /** Peak as a fraction of the window's mean level — the modulation depth. */
      depth: mean > 0 ? peak / mean : 0,
      // Zero, not Infinity, when the band is empty. A perfectly steady signal
      // has no energy anywhere after mean removal, so `peak / median` is 0/0 —
      // and reporting that as infinite evidence made a sustained tone the most
      // convincing trill the detector could see.
      ratio: median > 0 ? peak / median : 0,
      stability,
      trill: false,
    };

    hop.trill = this._commit(hop);
    this.hops.push(hop);
    if (this.hops.length > 64) this.hops.shift();
    return hop;
  }

  /**
   * Decide whether this hop is part of a trill, and emit the event when one
   * ends.
   *
   * The gates, in the order they eliminate things:
   *
   *   depth      the loudness has to actually pulse, by a fraction of its own
   *              level. Device-independent, unlike an absolute magnitude.
   *   ratio      the peak has to stand out of its own band. Kept, but at a
   *              threshold the recorded trill actually clears rather than the
   *              spec's 3x, which it does not.
   *   stability  the peak has to be in the same place as it was for the last
   *              few windows. This is the one that does the work.
   */
  _commit(hop) {
    const g = config.gesture.trill;
    const candidate =
      hop.depth >= g.MIN_DEPTH &&
      hop.ratio >= config.envelope.PEAK_OVER_MEDIAN &&
      hop.stability >= g.MIN_STABILITY;

    if (candidate) {
      if (!this.active) {
        this.active = { from: hop.t, rates: [], depths: [], hops: 0 };
      }
      this.active.to = hop.t;
      this.active.rates.push(hop.modRate);
      this.active.depths.push(hop.depth);
      this.active.hops++;
      // Committed only once hysteresis is satisfied. One hop is 683 ms of
      // window advanced by 85 ms — far too little evidence to call a rate
      // stable, since the first hop is compared against nothing.
      return this.active.hops >= config.gesture.trill.HYSTERESIS_HOPS;
    }

    if (this.active) {
      const done = this.active;
      this.active = null;
      if (done.hops >= config.gesture.trill.HYSTERESIS_HOPS) this._emit(done);
    }
    return false;
  }

  /** Turn a finished run of hops into one GestureEvent. */
  _emit(run) {
    const rates = [...run.rates].sort((a, b) => a - b);
    const modRate = rates[rates.length >> 1];
    // The run's hops each describe a 683 ms window, so the sound extends half a
    // window past the last hop's centre and half a window before the first.
    const from = run.from - this.windowMs / 2;
    const to = run.to + this.windowMs / 2;
    const dur = Math.max(0, to - from);

    const event = {
      t: from,
      type: 'trill',
      dur,
      // Modulation depth, which is already 0..1 and already device-independent.
      // How hard the lips were flapping, not how close the microphone was.
      intensity: Math.min(1, Math.max(...run.depths)),
      modRate,
      // The whole point of measuring the rate. Not a guess about how many r's
      // look emphatic — a count of how many times the lips actually closed.
      cycles: Math.max(1, Math.round((dur / 1000) * modRate)),
      place: 'unknown', // Phase 6 fills this in from vision
      detectLatency: config.latency.trill,
      confidence: stabilityOf(run.rates),
    };
    this.onGesture(event);
    return event;
  }

  /**
   * Close an in-progress trill and emit it.
   *
   * A trill's event cannot be emitted while it is happening, because `dur` and
   * `cycles` are not known until it stops — counting how many times the lips
   * closed requires them to have finished closing. So a run is emitted when it
   * ends, and if the microphone is stopped mid-trill nothing ends it. Call this
   * on teardown.
   *
   * The cost of that design, stated plainly: a long sustained trill appears
   * only once it is over, which for a two-second "brrrrr" is two seconds after
   * it began. That is inherent to reporting a measured count rather than a
   * guess, and it is why `detectLatency` alone does not describe this detector.
   *
   * @returns {?import('../core/types.js').GestureEvent}
   */
  flush() {
    if (!this.active) return null;
    const done = this.active;
    this.active = null;
    if (done.hops < config.gesture.trill.HYSTERESIS_HOPS) return null;
    return this._emit(done);
  }

  /** Latest hop, for the readout. */
  get latest() {
    return this.hops.at(-1) ?? null;
  }

  reset() {
    this.setRate(this.rate);
  }
}

export default EnvelopeAnalyser;
